import { isPhaseIn, isPhaseOut, isPreCr } from "@/utils/Lifecycle";
import numeral from "numeral";
import { headerWithUnitTemplate } from "./HeaderTemplates";

const nc12Header = {
  headerName: "12nc",
  field: "nc12",
  sortable: true,
  hide: false,
  width: 115
};

const heightHeader = {
  headerName: "Height",
  field: "housing.height",
  sortable: true,
  hide: false,
  width: 70,
  headerComponentParams: {
    template: headerWithUnitTemplate("mm")
  }
};

const heightHeaderInch = {
  headerName: "Height",
  field: "housing.height",
  sortable: true,
  hide: false,
  width: 70,
  headerComponentParams: {
    template: headerWithUnitTemplate("inch")
  }
};

const widthHeader = {
  headerName: "Width",
  field: "housing.width",
  sortable: true,
  hide: false,
  width: 70,
  headerComponentParams: {
    template: headerWithUnitTemplate("mm")
  }
};

const widthHeaderInch = {
  headerName: "Width",
  field: "housing.width",
  sortable: true,
  hide: false,
  width: 70,
  headerComponentParams: {
    template: headerWithUnitTemplate("inch")
  }
};

const lengthHeader = {
  headerName: "Length",
  field: "housing.length",
  sortable: true,
  hide: false,
  width: 70,
  headerComponentParams: {
    template: headerWithUnitTemplate("mm")
  }
};

const lengthHeaderInch = {
  headerName: "Length",
  field: "housing.length",
  sortable: true,
  hide: false,
  width: 70,
  headerComponentParams: {
    template: headerWithUnitTemplate("inch")
  }
};

const LESDiameterHeader = {
  headerName: "LES",
  field: "housing.dia",
  headerTooltip: "Diameter of Light Emitting Surface",
  sortable: true,
  hide: false,
  width: 65,
  valueFormatter: params => {
    return params.value === 0 ? "-" : params.value;
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("mm", true)
  }
};

const LESDiameterHeaderInch = {
  headerName: "LES",
  field: "housing.dia",
  headerTooltip: "Diameter of Light Emitting Surface",
  sortable: true,
  hide: false,
  width: 65,
  valueFormatter: params => {
    return params.value === 0 ? "-" : params.value;
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("inch", true)
  }
};

const thermalPowerHeader = {
  headerName: "Pth",
  field: "calculationResult.thermalPower",
  sortable: true,
  hide: false,
  width: 65,
  valueFormatter: params => {
    if (params.value < 10) {
      return numeral(params.value).format("0.0");
    }
    return Math.round(params.value);
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("W")
  }
};

const powerHeader = {
  headerName: "P",
  field: "calculationResult.power",
  sortable: true,
  hide: false,
  width: 60,
  valueFormatter: params => {
    return Math.round(params.value * 10) / 10;
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("W")
  }
};

const voltageHeader = {
  headerName: "Vf",
  field: "calculationResult.outputVoltageOfArray",
  sortable: true,
  hide: false,
  width: 60,
  valueFormatter: params => {
    return Math.round(params.value * 10) / 10;
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("V")
  }
};

const parallelHeader = {
  headerName: "P #",
  field: "calculationResult.numberOfModulesInParallel",
  sortable: true,
  hide: false,
  width: 80,
  headerTooltip: "Number in Parallel",
  headerComponentParams: {
    template: headerWithUnitTemplate(null, true)
  }
};

const seriesHeader = {
  headerName: "S #",
  field: "calculationResult.numberOfModulesInSeries",
  sortable: true,
  hide: false,
  width: 80,
  headerTooltip: "Number in Series",
  headerComponentParams: {
    template: headerWithUnitTemplate(null, true)
  }
};

const tcHeader = {
  headerName: "Tc",
  field: "calculationResult.temperature",
  sortable: true,
  hide: false,
  width: 60,
  headerComponentParams: {
    template: headerWithUnitTemplate("°C")
  }
};

const efficacyHeader = {
  headerName: "Efficacy",
  field: "calculationResult.luminousEfficacyOfModule",
  sortable: true,
  hide: false,
  width: 80,
  valueFormatter: params => {
    return Math.round(params.value);
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("lm/W")
  }
};

const currentHeader = {
  headerName: "I",
  field: "calculationResult.operatingCurrent",
  sortable: true,
  hide: false,
  width: 60,
  valueFormatter: params => {
    return Math.round(params.value * 1000);
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("mA")
  }
};

const fluxHeader = {
  headerName: "Flux",
  field: "calculationResult.outputFluxOfArray",
  sortable: true,
  hide: false,
  width: 65,
  valueFormatter: params => {
    return Math.round(params.value);
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("lm")
  }
};

const familyHeader = {
  headerName: "Application",
  field: "familyLevelOne",
  sortable: true,
  hide: false,
  width: 100
};

const dataSheetHeader = {
  headerName: "Datasheet",
  field: "datasheetUrl",
  sortable: true,
  hide: false,
  cellRenderer: params => {
    return `<a href="${params.value}" target="_blank">View</a>`;
  },
  width: 90
};

const nameHeader = {
  headerName: "Product Name",
  field: "name",
  sortable: true,
  hide: false,
  cellRenderer: params => {
    let output = params.value;

    if (params.data.phaseIn && isPhaseIn(params.data.phaseIn)) {
      output += '<span class="phase-in" > [new] </span>';
    }

    if (params.data.phaseOut && isPhaseOut(params.data.phaseOut)) {
      output += '<span class="phase-out"> [obsolete] </span>';
    }

    if (params.data.phaseIn && isPreCr(params.data.phaseIn)) {
      output += '<span class="pre-cr"> [coming soon]</span> ';
    }

    if (params.data.isArchived) {
      output += '<span class="is-archived"> [archived]</span>';
    }

    return output;
  },
  width: 300
};

const rthHeatsinkToAmbientHeader = {
  headerName: "Rth hs-a",
  field: "calculationResult.rthHeatsinkToAmbient",
  width: 85,
  sortable: true,
  hide: false,
  valueFormatter: params => {
    return numeral(params.value).format("0.00");
  },
  headerComponentParams: {
    template: headerWithUnitTemplate("°C/W")
  }
};

const countHeader = {
  headerName: "#",
  field: "totalNumberOfModulesInArray",
  headerTooltip: "Number of modules",
  width: 55,
  sortable: true,
  hide: false,
  headerComponentParams: {
    template: headerWithUnitTemplate("", true)
  }
};

export {
  countHeader,
  nameHeader,
  dataSheetHeader,
  familyHeader,
  fluxHeader,
  currentHeader,
  efficacyHeader,
  tcHeader,
  seriesHeader,
  parallelHeader,
  voltageHeader,
  powerHeader,
  thermalPowerHeader,
  LESDiameterHeader,
  LESDiameterHeaderInch,
  lengthHeader,
  lengthHeaderInch,
  widthHeader,
  widthHeaderInch,
  heightHeader,
  heightHeaderInch,
  nc12Header,
  rthHeatsinkToAmbientHeader
};
